import * as Sentry from '@sentry/react'
import { browserTracingIntegration } from '@sentry/react'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoPermissions } from 'features/permissions'
import snakeCase from 'lodash/snakeCase'
import mixpanel, { Dict } from 'mixpanel-browser'
import React, { useContext, useEffect } from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { LOGIN } from './index'
import { sessionRecordingControl } from './sessionRecordingControl'

type AnyValidPropertyValue =
  | string
  | number
  | boolean
  | object
  | undefined
  | null
  | Record<string, string | number | boolean | object | undefined | null>
type EventPropertiesType = Record<string, AnyValidPropertyValue>

type GeckoAnalyticsContextType = {
  trackButtonClicked: (
    properties: { button_name: string } & EventPropertiesType
  ) => void
  trackSliderChanged: (
    properties: { slider_name: string } & EventPropertiesType
  ) => void
  trackEvent: (eventName: string, eventProperties?: EventPropertiesType) => void
  combinedProps: Dict
}

const GeckoAnalyticsContext = React.createContext<
  GeckoAnalyticsContextType | undefined
>(undefined)

//------------------------------------------------------------------------------

/** A hook for accessing scoped analytics and reporting methods */
export const useGeckoAnalytics = (): GeckoAnalyticsContextType => {
  const context = useContext(GeckoAnalyticsContext)
  if (!context)
    throw new Error(
      'useGeckoAnalytics() must be used within GeckoAnalyticsProvider'
    )
  return context
}

//------------------------------------------------------------------------------

/**
 * A provider component offering scoped access to analytics/reporting. These
 * may be nested to describe a more granular view of the application's state
 * (for example, within specific extensions or sub-views)
 * @param children          Children of this pass-through component
 * @param props             Any data to attach to outgoing events
 * @constructor
 */
export const GeckoAnalyticsProvider: React.FC<
  { children: React.ReactNode } & Dict
> = ({ children, ...props }) => {
  const higherContext = React.useContext(GeckoAnalyticsContext)
  const { ENVIRONMENT, MIXPANEL_AUTH_TOKEN, SENTRY_DSN } = useRuntimeVariables()
  const { user: currentUser, hasPermissions } = useGeckoPermissions()
  const isInternalMember = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  // The top-level context should initialize Mixpanel and Sentry
  useEffect(() => {
    if (higherContext) return
    if (!currentUser) return
    // Recording stays ON for everyone by default; only Gecko-internal users can
    // disable their own session, so customers can't silence their telemetry.
    const recordersEnabled = isInternalMember
      ? sessionRecordingControl.isEnabled()
      : true
    const mixpanelSessionPercent =
      import.meta.env.MODE === 'production' && recordersEnabled ? 100 : 0

    mixpanel.init(MIXPANEL_AUTH_TOKEN, {
      persistence: 'localStorage',
      track_pageview: 'url-with-path-and-query-string',
      record_sessions_percent: mixpanelSessionPercent,
      record_mask_all_text: false,
      record_mask_all_inputs: false,
      record_heatmap_data: true,
    })
    mixpanel.register({ app: 'Forecast' })
    mixpanel.identify(currentUser.email)
    const userMetadata = currentUser
    mixpanel.people.set({
      $name: userMetadata.name,
      $email: userMetadata.email,
      user_id: userMetadata.id,
      ...(userMetadata.roles && {
        roles: userMetadata.roles.map((r) => r.name),
      }),
    })
    mixpanel.track(LOGIN.title)

    if (import.meta.env.MODE === 'production' && SENTRY_DSN !== '') {
      if (!Sentry.isInitialized()) {
        Sentry.init({
          dsn: SENTRY_DSN,
          debug: false,
          integrations: [
            browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: false,
              maskAllInputs: false,
              blockAllMedia: false,
            }),
            Sentry.replayCanvasIntegration(),
          ],
          replaysOnErrorSampleRate: recordersEnabled ? 1.0 : 0,
          replaysSessionSampleRate: recordersEnabled ? 1.0 : 0,
          environment: ENVIRONMENT,
          tracesSampleRate: 0.25,
        })
      }
      Sentry.setUser({ email: currentUser.email })
    }
  }, [
    higherContext,
    currentUser,
    isInternalMember,
    MIXPANEL_AUTH_TOKEN,
    SENTRY_DSN,
    ENVIRONMENT,
  ])

  const combinedProps = React.useMemo(
    () => forceSnakeCaseKeys({ ...props, ...higherContext?.combinedProps }),
    [higherContext, props]
  )

  const trackEvent = React.useCallback(
    (eventName: string, eventProperties?: Dict) => {
      mixpanel.track(
        eventName,
        !eventProperties
          ? combinedProps
          : { ...forceSnakeCaseKeys(eventProperties), ...combinedProps }
      )
    },
    [combinedProps]
  )

  const trackButtonClicked = React.useCallback(
    (properties: Dict) => trackEvent('Button Clicked', properties),
    [trackEvent]
  )

  const trackSliderChanged = React.useCallback(
    (properties: Dict) => trackEvent('Slider Changed', properties),
    [trackEvent]
  )

  const value = React.useMemo(
    () => ({
      trackButtonClicked,
      trackSliderChanged,
      trackEvent,
      combinedProps,
    }),
    [combinedProps, trackButtonClicked, trackSliderChanged, trackEvent]
  )

  return (
    <GeckoAnalyticsContext.Provider value={value}>
      {children}
    </GeckoAnalyticsContext.Provider>
  )
}

// The following code ensures that our event properties are standardized
// Shamelessly stolen from https://stackoverflow.com/questions/12931828
const forceSnakeCaseKeys = (obj: Dict): Dict =>
  obj !== null && obj !== undefined && obj.constructor === Object
    ? Object.keys(obj).reduce(
        (result, key) => ({
          ...result,
          [snakeCase(key)]: forceSnakeCaseKeys(obj[key]),
        }),
        {}
      )
    : obj
